import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
  FaArrowLeft,
  FaChevronDown,
  FaCloudUploadAlt,
  FaFileAlt,
  FaInfoCircle,
  FaPaperPlane,
  FaPlus,
  FaReceipt,
  FaSave,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import { EXPENSE_CATEGORIES } from "../../constants";
import Site from "../../models/Site";
import { sendRequest } from "../../utils/api";
import FullPageSpinner from "../../components/FullPageSpinner";
import { capitalizeWords } from "../../utils/string";
import { useModal } from "../../context/ConfirmModalContext";
import { useToast } from "../../context/ToastContext";
import { ExpenseBreakdown } from "../../models/expense/ExpenseBreakdown";
import { formatFileSize, isEmptyObject } from "../../utils/helper-function";
import FilePreviewModal from "../../Modal/AttachmentPreview";
import Attachment from "../../models/Attachment";
import { useAttachments } from "../../context/Attachments";


const categories = EXPENSE_CATEGORIES;


export default function CreateExpenseRequestPage() {
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { showToast } = useToast();

  const {
    attachments,
    handleFileChange,
    removeAttachment,
    clearAttachments,
  } = useAttachments(5);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(categories[0]);
  const [selectedSite, setSelectedSite] = useState<Site>({} as Site);
  const [requestedAmount, setRequestedAmount] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");

  const [sites, setSites] = useState<Site[]>([]);

  const [items, setItems] = useState<ExpenseBreakdown[]>([
    {
      id: "",
      title: "",
      amount: "",
    },
  ]);

  // const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    const getSite = async () => {
      try {
        setLoading(true);
        const sites = await sendRequest("GET", "/sites");
        setSites(sites);
        if (sites.length > 0) setSelectedSite(sites[0]);
      } catch (err: any) {

      } finally {
        setLoading(false);
      }
    }
    getSite();
  }, [])

  const handlePreview = (file: File) => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    const url = URL.createObjectURL(file);

    setPreviewFile(file);
    setPreviewUrl(url);
  };

  const handleClosePreview = () => {
    setPreviewFile(null);
    setPreviewUrl(null);
  };


  const formatAmount = (value: string) => {
    const cleaned = value.replace(/[^0-9.]/g, "");

    if (!cleaned) return "";

    const parts = cleaned.split(".");

    const integerPart = parts[0];
    const decimalPart = parts[1];

    const formattedInteger = Number(integerPart).toLocaleString("en-NG");

    if (decimalPart !== undefined) {
      return `${formattedInteger}.${decimalPart.slice(0, 2)}`;
    }

    return formattedInteger;
  };

  const parseAmount = (value: string) => {
    return Number(value.replace(/,/g, "")) || 0;
  };


  const addItem = () => {
    setItems((prev) => [
      ...prev,
      {
        id: uuidv4(),
        title: "",
        amount: "",
      },
    ]);
  };

  const removeItem = (id: string) => {
    if (items.length === 1) return;

    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const updateItem = (
    id: string,
    field: keyof ExpenseBreakdown,
    value: string
  ) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
            ...item,
            [field]:
              field === "amount" ? formatAmount(value) : value,
          }
          : item
      )
    );
  };

  // const handleFileChange = (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   const files = Array.from(event.target.files || []);

  //   const newAttachments = files.map((file) =>
  //     Object.assign(file, {
  //       id: uuidv4(),
  //       file,
  //     })
  //   );

  //   setAttachments((prev) => [...prev, ...newAttachments]);

  //   // Allow selecting the same file again
  //   event.target.value = "";
  // };

  // const removeAttachment = (id: string) => {
  //   setAttachments((prev) =>
  //     prev.filter((attachment) => attachment.id !== id)
  //   );
  // };

  const resetForm = () => {
    setTitle("");
    setCategory(categories[0]);
    setRequestedAmount("");
    setDescription("");
    setNotes("");
    // setAttachments([]);
    setItems([]);
    setSelectedSite(sites[0])
  }



  const totalItems = items.reduce(
    (total, item) => total + parseAmount(item.amount),
    0
  );

  const requestedTotal = parseAmount(requestedAmount);

  const breakdownExceedsRequest = totalItems > requestedTotal;


  const breakdownDoesNotMatchRequest =
    totalItems > 0 && requestedTotal > 0 && totalItems !== requestedTotal;

  const remainingAmount = requestedTotal - totalItems;

  // const hasIncompleteItems = items.some(
  //   (item) =>
  //     !item.title.trim() ||
  //     !item.amount ||
  //     parseAmount(item.amount) <= 0
  // );

  const handleSubmit = async (submitForApproval = true) => {
    if (!title.trim()) {
      alert("Please enter an expense title.");
      return;
    }

    else if (!category) {
      alert("Please select an expense category.");
      return;
    }

    else if (isEmptyObject(selectedSite)) {
      alert("Please select an expense site.");
      return;
    }

    else if (!requestedAmount || parseAmount(requestedAmount) <= 0) {
      alert("Please enter the requested amount.");
      return;
    }

    // if (hasIncompleteItems) {
    //   showToast(
    //     "Please complete all breakdown items or remove any unused rows.",
    //     { type: "error" }
    //   );
    //   return;
    // }

    if (breakdownDoesNotMatchRequest) {
      showToast(
        "The breakdown total must equal the requested amount. Requested: " +
        `${formatAmount(String(requestedTotal))}. ` +
        `Breakdown: ${formatAmount(String(totalItems))}.`,
        { type: "error" }
      );
      return;
    }

    if (breakdownExceedsRequest) {
      alert(
        `The breakdown total (₦${totalItems.toLocaleString("en-NG", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}) cannot exceed the requested amount (₦${requestedTotal.toLocaleString(
          "en-NG",
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }
        )}). Please adjust the breakdown.`
      );
      return;
    }
    const formData = new FormData();

    attachments.forEach((attachment) => {
      formData.append("attachments", attachment.file);
    });

    const data = {
      expense: {
        id: uuidv4(),
        title,
        category,
        siteId: selectedSite.id,
        requestedAmount: parseAmount(requestedAmount),
        description,
        notes,
        status: "pending",
      },
      items: items
        .filter((item) => item.title && item.amount)
        .map((item) => ({
          title: item.title,
          amount: parseAmount(item.amount),
        })),
    };

    formData.append("data", JSON.stringify(data));

    return openModal(
      "Submit Expense Request",
      "Are you sure you want to submit this expense request?",
      async () => {
        setLoading(true);
        setSubmitting(true);
        try {

          await sendRequest("POST", "/expenses", formData);
          showToast("Expense submitted successfully for review.", {
            type: "success",
          });

          resetForm();
        } catch (err: any) {
          console.error(err.response);

          showToast(
            err?.message ||
            "Failed to submit the expense request. Please check your network connection and try again.",
            { type: "error" }
          );
        } finally {
          setLoading(false);
          setSubmitting(false);
        }
      },
      true,
      "Yes, Submit",
      "Cancel"
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto">

        <FullPageSpinner show={loading} />

        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <button
              type="button"
              onClick={() => navigate("/expenses")}
              className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
            >
              {FaArrowLeft({
                size: 13,
                className: "",
              })}
            </button>

            <div>
              <h1 className="text-xl font-bold tracking-tight text-slate-800">
                Create Expense Request
              </h1>

              <p className="mt-1 text-sm text-slate-500">
                Request funds for an official business expense.
              </p>
            </div>
          </div>
        </div>

        {/* Important notice */}
        <div className="mb-6 flex gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-4">
          <div className="mt-0.5 text-blue-600">
            {FaInfoCircle({
              size: 15,
              className: "",
            })}
          </div>

          <div>
            <p className="text-sm font-semibold text-blue-800">
              Your requested amount is subject to approval
            </p>

            <p className="mt-1 text-xs leading-5 text-blue-700">
              Management may approve the full amount, reduce the amount,
              return the request for clarification, or reject the request.
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Basic information */}
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-5 py-4 sm:px-6">
              <h2 className="text-sm font-bold text-slate-800">
                Expense Information
              </h2>

              <p className="mt-1 text-xs text-slate-400">
                Provide the details of the expense you are requesting.
              </p>
            </div>

            <div className="grid gap-5 p-5 sm:grid-cols-2 sm:p-6">
              {/* Title */}
              <div className="sm:col-span-2">
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="block text-xs font-semibold text-slate-600">
                    Expense Title
                  </label>

                  <span
                    className={`text-[10px] ${title.length >= 25
                      ? "font-semibold text-amber-600"
                      : "text-slate-400"
                      }`}
                  >
                    {title.length}/25
                  </span>
                </div>

                <input
                  type="text"
                  required
                  maxLength={25}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Site transportation"
                  className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-50"
                />
              </div>

              {/* Category */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                  Expense Category
                </label>

                <div className="relative">
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="h-11 w-full appearance-none rounded-xl border border-slate-200 bg-white px-3 pr-10 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50"
                  >
                    {/* <option value="">Select category</option> */}

                    {categories.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>

                  <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {FaChevronDown({
                      size: 11,
                      className: "",
                    })}
                  </div>
                </div>
              </div>

              {/* Site */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                  Site / Location
                </label>

                <div className="relative">
                  <select
                    value={selectedSite?.name ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (value === "__create_site__") {
                        navigate("/settings/sites/create");
                        return;
                      }

                      const site = sites.find(
                        (item) => item.name.toLowerCase() === value.toLowerCase()
                      );

                      if (!site) return;

                      setSelectedSite(site);
                    }}
                    className="h-11 w-full appearance-none rounded-xl border border-slate-200 bg-white px-3 pr-10 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50"
                  >
                    {sites.length === 0 ? (
                      <option value="" disabled>
                        No sites available
                      </option>
                    ) : (
                      <option value="" disabled>
                        Select a site
                      </option>
                    )}

                    {sites.map((item) => (
                      <option key={item.id} value={item.name}>
                        {capitalizeWords(item.name)}
                      </option>
                    ))}

                    {sites.length === 0 && <option value="__create_site__">
                      + Create Site
                    </option>}
                  </select>

                  <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {FaChevronDown({ size: 11, className: "" })}
                  </div>
                </div>

                {sites.length === 0 && (
                  <p className="mt-1.5 text-[11px] text-slate-400">
                    You need to create a site before proceeding.
                  </p>
                )}
              </div>
              {/* Requested amount */}
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                  Requested Amount
                </label>

                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">
                    ₦
                  </span>

                  <input
                    type="text"
                    inputMode="decimal"
                    value={requestedAmount}
                    onChange={(e) =>
                      setRequestedAmount(formatAmount(e.target.value))
                    }
                    placeholder="0.00"
                    className="h-12 w-full rounded-xl border border-slate-200 bg-white pl-8 pr-4 text-base font-semibold text-slate-800 outline-none transition placeholder:text-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-50"
                  />
                </div>

                <p className="mt-1.5 text-[11px] text-slate-400">
                  This is the amount you are requesting. The final approved
                  amount may be lower.
                </p>
              </div>

              {/* Reason */}
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                  Reason / Business Justification (Optional)
                </label>

                <textarea
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Explain why this expense is required and how it relates to official work..."
                  className="w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-50"
                />
              </div>
            </div>
          </section>

          {/* Expense breakdown */}
          {requestedAmount && <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <div>
                <h2 className="text-sm font-bold text-slate-800">
                  Expense Breakdown
                </h2>

                {/* <p className="mt-1 text-xs text-slate-400">
                  Optionally provide a breakdown of the requested amount.
                </p> */}
                <p className="mt-1 text-xs text-slate-400">
                  Optionally provide a breakdown of the requested amount.
                  The breakdown total must not exceed the requested amount.
                </p>
              </div>

              <button
                type="button"
                onClick={addItem}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 text-xs font-semibold text-white transition hover:bg-blue-700"
              >
                {FaPlus({
                  size: 10,
                  className: "",
                })}
                Add Item
              </button>
            </div>

            <div className="space-y-3 p-5 sm:p-6">
              {items.map((item, index) => (
                <div
                  key={item.id}
                  className="grid gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3 sm:grid-cols-[1fr_180px_40px] sm:items-end"
                >
                  <div>
                    <div className="mb-1.5 flex items-center justify-between">
                      <label className="block text-[11px] font-semibold text-slate-500">
                        Description
                      </label>

                      <span className="text-[10px] font-medium text-slate-400">
                        {item.title.length}/20
                      </span>
                    </div>

                    <input
                      type="text"
                      maxLength={20}
                      value={item.title}
                      onChange={(e) =>
                        updateItem(item.id, "title", e.target.value)
                      }
                      placeholder={`Expense item ${index + 1}`}
                      className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-xs text-slate-700 outline-none transition placeholder:text-slate-300 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-[11px] font-semibold text-slate-500">
                      Amount
                    </label>

                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                        ₦
                      </span>

                      <input
                        type="text"
                        inputMode="decimal"
                        value={item.amount}
                        onChange={(e) =>
                          updateItem(
                            item.id,
                            "amount",
                            e.target.value
                          )
                        }
                        placeholder="0.00"
                        className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 text-xs font-semibold text-slate-700 outline-none transition placeholder:text-slate-300 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    disabled={items.length === 1}
                    className="flex h-10 w-full items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-400 transition hover:border-red-200 hover:bg-red-50 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-40 sm:w-10"
                  >
                    {FaTrash({
                      size: 11,
                      className: "",
                    })}
                  </button>
                </div>
              ))}

              {/* Breakdown validation */}
              {requestedTotal > 0 && (
                <div
                  className={`rounded-xl border p-4 ${breakdownExceedsRequest
                    ? "border-red-200 bg-red-50"
                    : "border-slate-200 bg-slate-50"
                    }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`mt-0.5 ${breakdownExceedsRequest
                        ? "text-red-600"
                        : "text-slate-500"
                        }`}
                    >
                      {FaInfoCircle({ size: 15, className: "" })}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p
                        className={`text-xs font-semibold ${breakdownExceedsRequest
                          ? "text-red-700"
                          : "text-slate-700"
                          }`}
                      >
                        {breakdownExceedsRequest
                          ? "Breakdown exceeds requested amount"
                          : "Breakdown within requested amount"}
                      </p>

                      <p
                        className={`mt-1 text-xs leading-5 ${breakdownExceedsRequest
                          ? "text-red-600"
                          : "text-slate-500"
                          }`}
                      >
                        {breakdownExceedsRequest
                          ? `Your breakdown exceeds the requested amount by ₦${Math.abs(
                            remainingAmount
                          ).toLocaleString("en-NG", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}. Please reduce the item amounts.`
                          : `₦${remainingAmount.toLocaleString("en-NG", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} remaining to allocate.`}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {totalItems > 0 && (
                <div className="flex items-center justify-between border-t border-slate-100 pt-4">
                  <span className="text-xs font-medium text-slate-500">
                    Breakdown Total
                  </span>

                  <span
                    className={`text-sm font-bold ${breakdownExceedsRequest
                      ? "text-red-600"
                      : "text-slate-800"
                      }`}
                  >
                    ₦
                    {totalItems.toLocaleString("en-NG", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              )}
            </div>
          </section>}

          {/* Supporting documents */}
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-5 py-4 sm:px-6">
              <h2 className="text-sm font-bold text-slate-800">
                Supporting Documents
              </h2>

              <p className="mt-1 text-xs text-slate-400">
                Attach quotations, receipts, invoices, approval evidence or
                other relevant documents.
              </p>
            </div>

            <div className="p-5 sm:p-6">
              <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center transition hover:border-blue-400 hover:bg-blue-50">
                <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
                  {FaCloudUploadAlt({
                    size: 18,
                    className: "",
                  })}
                </div>

                <p className="text-xs font-semibold text-slate-700">
                  Click to upload documents
                </p>

                <p className="mt-1 text-[11px] text-slate-400">
                  PDF, JPG, PNG or DOCX · Maximum 10MB per file
                </p>

                <input
                  type="file"
                  accept="image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  multiple
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>

              {/* Supporting Documents - Coming Soon */}
              {/*<div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 px-5 py-8 text-center">
                <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-slate-200 text-slate-500">
                  {FaCloudUploadAlt({
                    size: 18,
                    className: "",
                  })}
                </div>

                <span className="mb-2 rounded-full bg-amber-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                  Coming Soon
                </span>

                <p className="text-xs font-semibold text-slate-700">
                  Document uploads are not available yet
                </p>

                <p className="mt-1 max-w-sm text-[11px] leading-5 text-slate-400">
                  This feature is currently under development and will be available
                  in a future update.
                </p>
              </div>*/}

              {attachments.length > 0 && (
                <div className="mt-4 space-y-2">
                  {attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white p-3"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                        {FaFileAlt({
                          size: 14,
                          className: "",
                        })}
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-semibold text-slate-700">
                          {attachment?.file?.name}
                        </p>

                        <p className="mt-0.5 text-[10px] text-slate-400">
                          {formatFileSize(attachment?.file?.size)}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => handlePreview(attachment.file)}
                        className="flex h-8 items-center justify-center rounded-lg px-3
             text-xs font-medium text-blue-600 transition
             hover:bg-blue-50"
                      >
                        Preview
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          removeAttachment(attachment.id)
                        }
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-red-50 hover:text-red-500"
                      >
                        {FaTimes({
                          size: 11,
                          className: "",
                        })}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Additional notes */}
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-5 py-4 sm:px-6">
              <h2 className="text-sm font-bold text-slate-800">
                Additional Notes
              </h2>
            </div>

            <div className="p-5 sm:p-6">
              <textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional information that may help with the review..."
                className="w-full resize-none rounded-xl border border-slate-200 px-3 py-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-50"
              />
            </div>
          </section>

          {/* Approval workflow */}
          <section className="rounded-2xl border border-blue-100 bg-blue-50/60 p-5 sm:p-6">
            <div className="flex gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white">
                {FaReceipt({
                  size: 14,
                  className: "",
                })}
              </div>

              <div>
                <h3 className="text-sm font-bold text-slate-800">
                  Approval Workflow
                </h3>

                <p className="mt-1 text-xs leading-5 text-slate-500">
                  Once submitted, this request will go through the company's
                  approval process.
                </p>

                <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px] font-semibold">
                  {/* <span className="rounded-full bg-white px-3 py-1.5 text-blue-700 shadow-sm">
                    Project Manager
                  </span>

                  <span className="text-slate-300">→</span>

                  <span className="rounded-full bg-white px-3 py-1.5 text-blue-700 shadow-sm">
                    Finance
                  </span> */}

                  {/* <span className="text-slate-300">→</span> */}

                  <span className="rounded-full bg-white px-3 py-1.5 text-blue-700 shadow-sm">
                    HRM
                  </span>

                  <span className="text-slate-300">→</span>

                  <span className="rounded-full bg-white px-3 py-1.5 text-blue-700 shadow-sm">
                    Managing Director
                  </span>

                  <span className="text-slate-300">→</span>

                  <span className="rounded-full bg-white px-3 py-1.5 text-emerald-700 shadow-sm">
                    Payment
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Actions */}
          <div className="flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={() => navigate("/expenses")}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
            >
              {FaTimes({
                size: 11,
                className: "",
              })}
              Cancel
            </button>

            <div className="flex flex-col gap-3 sm:flex-row">
              {/* <button
                type="button"
                disabled={submitting}
                onClick={() => handleSubmit(false)}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-blue-200 bg-white px-5 text-sm font-semibold text-blue-600 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {FaSave({
                  size: 12,
                  className: "",
                })}
                Save as Draft
              </button> */}

              <button
                type="button"
                disabled={submitting}
                onClick={() => handleSubmit(true)}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {FaPaperPlane({
                  size: 12,
                  className: "",
                })}
                {submitting ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </div>

          <FilePreviewModal
            file={previewFile}
            previewUrl={previewUrl}
            onClose={handleClosePreview}
          />

        </div>
      </div>
    </div>
  );
}